import { useEffect, useState } from "react";
import { BrandLogo } from "@/components/common/BrandLogo";
import { Button } from "@/components/ui/button";
import { FileDown, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { SECTIONS, publicPdfUrl, scrollToSection, stateMeta, TONE_DOT } from "./proposalUtils";

/** Sticky glass header: brand, section nav (desktop), PDF + primary action. Tracks the active section while scrolling. */
export const ProposalHeader = ({ data, onAccept }) => {
  const [active, setActive] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const meta = stateMeta(data);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);

  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: [0, 0.2, 0.6] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [data]);

  return (
    <header className={cn("proposal-no-print sticky top-0 z-40 border-b transition-colors duration-200", scrolled ? "border-border/70 bg-background/85 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70" : "border-transparent bg-transparent")} data-testid="proposal-header">
      <div className="proposal-progress absolute inset-x-0 bottom-[-1px] h-[2px] bg-primary" style={{ transform: `scaleX(${progress})`, opacity: scrolled ? 1 : 0 }} aria-hidden="true" data-testid="proposal-progress" />
      <div className="mx-auto flex h-16 max-w-[1180px] items-center justify-between gap-4 px-5 sm:px-8">
        <div className={cn("transition-colors duration-200", !scrolled && "text-white [&_.text-foreground]:text-white [&_.text-muted-foreground]:text-white/60")}>
          <BrandLogo subtitle="Tilbud" />
        </div>
        <nav className={cn("hidden items-center gap-1 lg:flex", !scrolled && "text-white")} aria-label="Seksjoner" data-testid="proposal-section-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className={cn(
                "relative h-9 rounded-full px-3.5 text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
                scrolled ? "text-muted-foreground hover:text-foreground hover:bg-muted" : "text-white/70 hover:text-white hover:bg-white/10",
                active === s.id && (scrolled ? "text-foreground bg-muted" : "text-white bg-white/10"),
              )}
              aria-current={active === s.id ? "true" : undefined}
              data-testid={`proposal-nav-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" className={cn("h-9 px-3 text-[13.5px]", !scrolled && "text-white/85 hover:bg-white/10 hover:text-white")}>
            <a href={publicPdfUrl(data.token)} target="_blank" rel="noopener noreferrer" data-testid="proposal-pdf-link"><FileDown /> <span className="hidden sm:inline">PDF</span></a>
          </Button>
          {data.can_accept ? (
            <Button onClick={onAccept} className="hidden h-9 rounded-lg px-4 text-[13.5px] shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.6)] sm:inline-flex" data-testid="proposal-header-accept-button">Aksepter tilbud</Button>
          ) : (
            <span className={cn("hidden h-9 items-center gap-2 rounded-full px-3.5 text-[13px] font-medium ring-1 sm:inline-flex", scrolled ? "bg-muted text-foreground ring-border" : "bg-white/10 text-white ring-white/15")} data-testid="proposal-header-state">
              {data.state === "accepted" ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <span className={cn("h-2 w-2 rounded-full", TONE_DOT[meta.tone])} />}
              {meta.short}
            </span>
          )}
        </div>
      </div>
    </header>
  );
};
